/**
 * SiteTracker
 * -----------
 * Silently fires a pageview hit to /api/track/pageview on every route change.
 * - Generates/persists a session ID in sessionStorage
 * - Reads UTM params from the URL automatically
 * - Does NOT block rendering; errors are swallowed silently
 */
import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import api from '../../services/api';

// Produce a random hex session id and persist it for the browser tab lifetime
const getSessionId = () => {
  const KEY = 'sv_session';
  let sid = sessionStorage.getItem(KEY);
  if (!sid) {
    sid = Array.from(crypto.getRandomValues(new Uint8Array(24)))
      .map(b => b.toString(16).padStart(2, '0'))
      .join('');
    sessionStorage.setItem(KEY, sid);
  }
  return sid;
};

const SiteTracker = () => {
  const location    = useLocation();
  const lastPage    = useRef(null);

  useEffect(() => {
    const page = location.pathname + location.search;

    // Debounce: don't re-fire if navigating to the exact same path+query
    if (page === lastPage.current) return;
    lastPage.current = page;

    const referrer = document.referrer || '';
    const params   = new URLSearchParams(location.search);

    const payload = {
      session_id:   getSessionId(),
      page,
      referrer,
      screen_width: window.innerWidth,
      utm_source:   params.get('utm_source')   || undefined,
      utm_medium:   params.get('utm_medium')   || undefined,
      utm_campaign: params.get('utm_campaign') || undefined,
    };

    // Fire-and-forget – never block navigation or throw UI errors
    api.post('/track/pageview', payload).catch(() => {});
  }, [location]);

  return null; // renders nothing
};

export default SiteTracker;
